import {
  Box,
  Center,
  Flex,
  Heading,
  Image,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  Wrap,
} from '@chakra-ui/react';
import CaptureButton from 'components/CaptureButton';
import CompanyInfo from 'components/CompanyInfo';
import PreviewImageModal from 'components/PreviewImageModal';
import RadioButtons from 'components/RadioButtons';
import { captureListState } from 'lib/atoms/orderState';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import history from 'router/history';
import BottomFixedButton from '../../components/BottomFixedButton';
import imageCompression from 'browser-image-compression';
import { setImageMint, setItemCheck } from './api';
import { ResStatus } from 'lib/api/enum';
import withAuth from 'lib/hook/withAuth';
import usePathNavigate from 'lib/hook/usePathNavigate';

function RequestEstimate() {
  const toast = useToast();
  const { pathNavigate } = usePathNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);

  const [categoryMenu, setCategoryMenu] = useState<string[]>(['가방']);
  const [categoryId, setCategoryId] = useState(-1);
  const [menuIdList, setMenuIdList] = useState<number[]>([]);
  const onCategoryHandler = (value: string) => {
    console.log(value);
    const category = categoryMenu.find((menu) => menu === value);
    if (category !== undefined) {
      setCategoryId(3);
      setMenuIdList([3001]);
    }
  };

  const [captureList, setCaptureList] = useRecoilState(captureListState);
  useEffect(() => {
    const listenBackEvent = () => {
      setCaptureList([]);
    };
    const unlistenHistoryEvent = history.listen(({ action }) => {
      if (action === 'POP') {
        listenBackEvent();
      }
    });
    return unlistenHistoryEvent;
  }, [history]);

  const {
    isOpen: imageIsOpen,
    onClose: imageClose,
    onOpen: imageOpen,
  } = useDisclosure();
  const [previewImage, setPreviewImage] = useState('');
  const onPreviewImage = (previewImage: string) => {
    console.log(previewImage);
    setPreviewImage(previewImage);
    imageOpen();
  };

  const [requestsValue, setRequestsValue] = useState('');
  const onChangeRequestsValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRequestsValue(e.target.value);
  };

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const uploadImages = async () => {
    if (captureList.length > 0) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
        fileType: 'image/jpeg',
      };
      const responseList = await Promise.all(
        captureList.map(async (pic, index) => {
          const compressedFile = await imageCompression(pic.imageFile, options);
          const base64File: string = await imageCompression.getDataUrlFromFile(
            compressedFile
          );
          const result = await setImageMint({
            files: base64File.replace('data:image/jpeg;base64,', ''),
            contents_type: 'order',
          });
          if (result.status !== ResStatus.RES_SUCCESS) {
            return -1;
          }
          return result.command.image_id;
        })
      );
      return responseList;
    }
    return [];
  };
  const onNextHandler = async () => {
    setIsSubmitLoading(true);
    try {
      const uploadResults = await uploadImages();
      const imageIdList = uploadResults.filter((imageId) => imageId !== -1);

      if (
        imageIdList &&
        imageIdList.length > 0 &&
        uploadResults.length === imageIdList.length
      ) {
        const result = await setItemCheck({
          service_id: menuIdList,
          image_id: imageIdList,
          comment: requestsValue,
          order_price: 0,
        });
        if (result?.status === ResStatus.RES_SUCCESS) {
          setCaptureList([]);
          setIsSubmitLoading(false);
          pathNavigate('/requests/estimate/pass');
        } else {
          throw new Error(result?.status + '/' + result.result_message);
        }
      } else {
        throw new Error('이미지 업로드 실패');
      }
    } catch (err: any) {
      toast({
        title: '오류',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setIsSubmitLoading(false);
    }
  };

  return (
    <Box>
      <Center my={'18px'} fontSize="20px" fontWeight={'semibold'}>
        수선견적
      </Center>
      <Box alignItems={'flex-start'} mt={'36px'} mb={'20px'} px={'20px'}>
        <Heading fontSize={'16px'} fontWeight="semibold" mb={'16px'}>
          1. 견적 제품의 카테고리를 선택하세요
        </Heading>
        {categoryMenu && (
          <RadioButtons
            radioGroupName="category"
            buttonList={categoryMenu}
            buttonSize={{ w: '105px', h: '56px' }}
            onChange={onCategoryHandler}
          />
        )}
      </Box>
      <Box w={'100%'} h="8px" bg="#f5f6f8" />
      <Box my={'20px'} px={'20px'}>
        <Heading fontSize={'16px'} fontWeight="semibold" mb={'16px'}>
          2. 요청하실 제품 사진을 추가해 주세요
        </Heading>
        <Box>
          <Wrap spacing={'5px'} overflow="visible">
            <CaptureButton
              slotId={0}
              onPreviewImage={onPreviewImage}
              setLoading={setLoading}
            />
            <CaptureButton
              slotId={1}
              onPreviewImage={onPreviewImage}
              setLoading={setLoading}
            />
            <CaptureButton
              slotId={2}
              onPreviewImage={onPreviewImage}
              setLoading={setLoading}
            />
            <CaptureButton
              slotId={3}
              onPreviewImage={onPreviewImage}
              setLoading={setLoading}
            />
          </Wrap>
          <Flex mt={'9px'} fontSize="12px" color="#128cfe">
            <Image src="/assets/images/icn_marks_exclamation_blue.svg" />
            <Text fontWeight={'semibold'} ml="0.25rem">
              최소 2장 이상
            </Text>
            <Text>의 사진을 업로드해 주세요.(최대 4장)</Text>
          </Flex>
        </Box>
      </Box>
      <Box w={'100%'} h="8px" bg="#f5f6f8" />
      <Box w="100%" mt={'20px'} px={'20px'}>
        <Heading fontSize={'16px'} fontWeight="semibold" mb={'16px'}>
          3. 견적 요청사항을 입력해주세요
        </Heading>
        <Textarea
          w={'100%'}
          h={'108px'}
          mb="19px"
          resize="vertical"
          fontSize={'14px'}
          placeholder="최대 100자까지 작성 가능해요."
          value={requestsValue}
          onChange={onChangeRequestsValue}
          maxLength={100}
        />
      </Box>
      <Box w={'100%'} h="41px" />
      <CompanyInfo />
      <Box h="80px" />
      <BottomFixedButton
        isLoading={isSubmitLoading}
        isDisabled={
          categoryId < 0 || captureList.length < 2 || requestsValue === ''
        }
        text="다음"
        colorScheme="mint"
        onClick={onNextHandler}
      />
      <PreviewImageModal
        isOpen={imageIsOpen}
        onClose={imageClose}
        previewImage={previewImage}
      />
    </Box>
  );
}
export default withAuth(RequestEstimate);
